// react
import { useCallback, useRef } from 'react';

// mui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Slide, { SlideProps } from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type Variant = 'success' | 'error' | 'warning' | 'info' | 'plain';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  title: string;
  description?: string;
  variant?: Variant;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  duration?: number;
  isOverrideBottom?: boolean;
  action?: ToastAction;
}

const SWIPE_THRESHOLD = 80;

const SlideTransition = (props: SlideProps) => (
  <Slide {...props} direction='left' />
);

/**
 * Toast provides a stylised notification.
 */
export const Toast = ({
  title,
  description,
  isOpen = false,
  setIsOpen,
  variant = 'plain',
  duration = 5000,
  isOverrideBottom = false,
  action,
}: ToastProps) => {
  const severity = variant === 'plain' ? 'info' : variant;

  const containerRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const isDragging = useRef(false);

  const handleClose = (
    _event?: React.SyntheticEvent | Event | null,
    reason?: string,
  ) => {
    if (reason === 'clickaway') return;
    setIsOpen(false);
  };

  const getClientX = (e: React.TouchEvent | React.MouseEvent) =>
    'touches' in e ? e.touches[0].clientX : e.clientX;

  const getEndClientX = (e: React.TouchEvent | React.MouseEvent) =>
    'changedTouches' in e ? e.changedTouches[0].clientX : e.clientX;

  const onDragStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    isDragging.current = true;
    startX.current = getClientX(e);
    currentX.current = 0;
    if (containerRef.current) {
      containerRef.current.style.transition = 'none';
    }
  }, []);

  const onDragMove = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;
    const delta = getClientX(e) - startX.current;
    const offset = Math.max(0, delta);
    currentX.current = offset;
    containerRef.current.style.transform = `translateX(${offset}px)`;
    containerRef.current.style.opacity = `${1 - offset / 300}`;
  }, []);

  const onDragEnd = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (!isDragging.current || !containerRef.current) return;
      isDragging.current = false;

      const delta = getEndClientX(e) - startX.current;
      if (delta > SWIPE_THRESHOLD) {
        containerRef.current.style.transition =
          'transform 200ms, opacity 200ms';
        containerRef.current.style.transform = 'translateX(100%)';
        containerRef.current.style.opacity = '0';
        setTimeout(() => setIsOpen(false), 200);
      } else {
        containerRef.current.style.transition =
          'transform 200ms, opacity 200ms';
        containerRef.current.style.transform = 'translateX(0)';
        containerRef.current.style.opacity = '1';
      }
    },
    [setIsOpen],
  );

  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={duration}
      onClose={handleClose}
      slots={{ transition: SlideTransition }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      sx={{
        bottom: isOverrideBottom ? undefined : { xs: 80, sm: 24 },
      }}
    >
      <Box
        ref={containerRef}
        onTouchStart={onDragStart}
        onTouchMove={onDragMove}
        onTouchEnd={onDragEnd}
        onMouseDown={onDragStart}
        onMouseMove={onDragMove}
        onMouseUp={onDragEnd}
        onMouseLeave={onDragEnd}
        sx={{
          cursor: 'grab',
          userSelect: 'none',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          sx={
            variant === 'plain'
              ? {
                  bgcolor: 'grey.200',
                  color: 'text.primary',
                  '& .MuiAlert-icon': { display: 'none' },
                  '& .MuiAlert-action svg': { color: 'text.primary' },
                }
              : undefined
          }
        >
          <Stack
            sx={{
              justifyContent: 'center',
              alignItems: 'flex-start',
            }}
          >
            <Typography
              variant='subtitle2'
              sx={{
                color: 'inherit',
              }}
            >
              {title}
            </Typography>
            {description && (
              <Typography
                variant='caption'
                sx={{
                  color: 'inherit',
                  mt: 0.5,
                }}
              >
                {description}
              </Typography>
            )}
            {action && (
              <Button
                size='small'
                onClick={action.onClick}
                sx={{
                  mt: 0.5,
                  color: 'inherit',
                }}
              >
                {action.label}
              </Button>
            )}
            {variant === 'error' && !action && (
              <Button
                href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}?subject=${encodeURIComponent('Support request')}`}
                size='small'
                sx={(theme) => ({
                  mt: 0.5,
                  color: theme.palette.error.light,
                })}
              >
                Contact support
              </Button>
            )}
          </Stack>
        </Alert>
      </Box>
    </Snackbar>
  );
};

Toast.componentName = 'Toast';

export default Toast;
